import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { ScreenModel } from '@hmedia/legenda-ds-api-client'
import { Modal, Switch, Table, TableColumnType, message } from 'antd'
import { FC, useMemo } from 'react'
import { useQueries, useQueryClient } from 'react-query'
import { useScreensQuery } from '../screens/queries'
import { switchChildrens } from '../uniqueDepartures/UniqueDeparturesView'
import type { CustomContentAdminDTO } from './models'
import { useAssignCustomContentToScreenMutation, useRemoveCustomContentFromScreenMutation } from './queries'
import API from '../../services/DSApiService'

type Props = {
  visible: boolean
  content?: CustomContentAdminDTO
  onClose: (e) => void
}

export const ScreenAssignmentModal: FC<Props> = ({ visible, onClose, content }) => {
  const screensQuery = useScreensQuery()
  const queryClient = useQueryClient()

  // Filter screens to only show those with custom content permission enabled
  const filteredScreens = useMemo(() => {
    return (screensQuery.data || []).filter(screen => screen.customContentEnabled)
  }, [screensQuery.data])

  // Fetch custom content assignments for all filtered screens in parallel
  const screenAssignmentQueries = useQueries(
    filteredScreens.map((screen) => ({
      queryKey: ['customContentsForScreen', screen.id],
      queryFn: async () => {
        const response = await API.getCustomContentsForScreen(screen.id)
        return {
          screenId: screen.id,
          customContents: response.data,
        }
      },
      enabled: !!content && visible,
    }))
  )

  // Build a map of screenId -> isAssigned for efficient lookup
  const assignmentMap = useMemo(() => {
    const map = new Map<string, boolean>()
    screenAssignmentQueries.forEach((query) => {
      if (query.data) {
        const isAssigned = query.data.customContents.some(
          (cc) => cc.id === content?.id
        )
        map.set(query.data.screenId, isAssigned)
      }
    })
    return map
  }, [screenAssignmentQueries, content?.id])

  // Check if any assignment query is still loading
  const isLoadingAssignments = screenAssignmentQueries.some((q) => q.isLoading)

  const assignMutation = useAssignCustomContentToScreenMutation(
    () => {
      message.success('Képernyő hozzárendelés frissítve')
      // Invalidate all screen assignment queries to refresh the UI
      queryClient.invalidateQueries(['customContentsForScreen'])
    },
    (error) => {
      message.error('Hiba történt a hozzárendelés frissítésénél')
      console.error(error)
    }
  )

  const removeMutation = useRemoveCustomContentFromScreenMutation(
    () => {
      message.success('Képernyő hozzárendelés frissítve')
      // Invalidate all screen assignment queries to refresh the UI
      queryClient.invalidateQueries(['customContentsForScreen'])
    },
    (error) => {
      message.error('Hiba történt a hozzárendelés frissítésénél')
      console.error(error)
    }
  )

  const onChangeOne = async (screenId: string, enabled: boolean) => {
    if (!content) return

    if (enabled) {
      // Ellenőrizzük, hogy van-e már hozzárendelés
      try {
        const checkResponse = await API.checkScreenHasCustomContentAssignment(screenId)

        if (checkResponse.data.hasAssignment &&
            checkResponse.data.currentAssignment?.customContentId !== content.id) {
          // Megerősítés kérése a felhasználótól
          Modal.confirm({
            title: 'Meglévő hozzárendelés felülírása',
            icon: <ExclamationCircleOutlined />,
            content: (
              <div>
                <p>Ez a képernyő már hozzá van rendelve egy másik egyedi tartalomhoz:</p>
                <p><strong>"{checkResponse.data.currentAssignment?.customContentName}"</strong></p>
                <p style={{ marginTop: 12 }}>
                  Ha folytatod, a korábbi hozzárendelés inaktívvá válik,
                  és ezt az egyedi tartalmat fogja megjeleníteni a képernyő.
                </p>
                <p style={{ marginTop: 12, color: '#888' }}>
                  <small>Megjegyzés: A korábbi hozzárendelés megmarad az adatbázisban
                  inaktív állapotban, így később visszaállítható.</small>
                </p>
              </div>
            ),
            okText: 'Folytatás',
            cancelText: 'Mégse',
            onOk: () => {
              assignMutation.mutate({
                screenId,
                request: { customContentId: content.id },
              })
            },
          })
          return
        }
      } catch (error) {
        console.error('Error checking assignment:', error)
        // Ha hiba van az ellenőrzésben, folytatjuk a hozzárendelést
      }

      // Ha nincs konfliktus vagy ugyanazt rendeljük hozzá újra
      assignMutation.mutate({
        screenId,
        request: { customContentId: content.id },
      })
    } else {
      // Remove esetén nincs szükség figyelmeztetésre
      removeMutation.mutate({
        screenId,
        request: { customContentId: content.id },
      })
    }
  }

  const tableCols: TableColumnType<ScreenModel>[] = [
    {
      title: 'Kijelző neve',
      dataIndex: 'name',
    },
    {
      title: 'GATE',
      dataIndex: 'gate',
      width: '100px',
      render: (gate: string | undefined) => gate || '-',
    },
    {
      title: 'Jelenlegi tartalom',
      dataIndex: 'id',
      width: '200px',
      render: (screenId: string) => {
        const query = screenAssignmentQueries.find(
          q => q.data?.screenId === screenId
        )
        const currentContent = query?.data?.customContents[0]

        if (!currentContent) return '-'

        const isCurrentContent = currentContent.id === content?.id
        return (
          <span style={{
            color: isCurrentContent ? '#52c41a' : '#faad14',
            fontWeight: isCurrentContent ? 'bold' : 'normal'
          }}>
            {currentContent.name}
            {!isCurrentContent && ' ⚠️'}
          </span>
        )
      },
    },
    {
      title: 'Engedélyezve?',
      dataIndex: 'id',
      align: 'right',
      width: '130px',
      render: (screenId: string, screen) => {
        const isEnabled = assignmentMap.get(screenId) || false

        return (
          <Switch
            {...switchChildrens}
            loading={assignMutation.isLoading || removeMutation.isLoading || isLoadingAssignments}
            checked={isEnabled}
            onChange={(value) => {
              onChangeOne(screenId, value)
            }}
          />
        )
      },
    },
  ]

  return (
    <Modal
      title="Egyedi tartalom megjelenítése az alábbi kijelző(k)ön"
      open={visible}
      width={600}
      closeIcon={<CloseOutlined />}
      closable={true}
      onCancel={onClose}
      footer={null}
      maskClosable={true}
    >
      <p>
        Kiválasztott tartalom: <strong>"{content?.name}"</strong>
      </p>
      <Table
        pagination={false}
        rowKey={(scr) => scr.id}
        scroll={{ y: '60vh' }}
        columns={tableCols}
        size="small"
        loading={screensQuery.isLoading}
        dataSource={filteredScreens}
      />
    </Modal>
  )
}

export default ScreenAssignmentModal
